import { useCallback, useEffect, useState } from "react";
import { END_DATE, FocusedInput, OnDatesChangeProps, START_DATE, useDatepicker } from "@datepicker-react/hooks";
import { withErrorBoundary } from "@sentry/nextjs";
import { addDays, differenceInDays, format, parseISO, toDate } from "date-fns";
import { usePathname, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { CancellationPolicyType } from "@/constant";
import { PropertyDataFromBooking, PropertyTypeV2 } from "commonModule/services/common";
import { CalendarAndGuestContols } from "components/date-picker/date-range-input/CalendarAndGuestContols";
import { closeDetailPageCalendar } from "components/date-picker/date-range-input/CalendarPopOver";
import DateRangeInputDetail from "components/date-picker/date-range-input/date-range-input-detail";
import Divider from "components/utilities/Divider";
import { selectUser } from "stores/auth/authSlice";
import { clearPrecisionPricingData, markBookingStore, registerClickDateChangeEventMixPanel, selectBookingStore } from "stores/booking/bookingSlice";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
import { calculateFormattedBeforeDate } from "utils/property-utils";
import { clearQueryStringParameter, removeQueryParameters, updateQueryStringParameter } from "utils/url";
import { GuestModule } from "./GuestModule";
export interface DateSelectorProps {
  property: PropertyTypeV2 | PropertyDataFromBooking;
  usedFromContactHost?: boolean;
  isDisplayClearDates?: boolean;
  displayAllInPricing?: boolean;
}
const DateInputmodalHeader = () => {
  const t = useTranslations("detailPage");
  const bookingStore = useAppSelector(selectBookingStore);
  return <div data-sentry-component="DateInputmodalHeader" data-sentry-source-file="DateSelector.tsx">
      {/* {!isPaymentPage && (
        <button
          aria-label='Reset Dates'
          onClick={handleDateReset}
          className='btn btn-ghost p-0 text-base font-semibold underline hover:text-black-900'
        >
          {c("words.clear")}
        </button>
       )} */}
      <p className='mb-2.5 text-lg font-semibold leading-[18px] tracking-[-0.18px] text-black-900'>
        {bookingStore.startDate && bookingStore.endDate ? `${bookingStore.dayCount} ${bookingStore.dayCount > 1 ? "nights" : "night"}` : t("words.checkin.dates")}
        {/* Select Check-in date */}
      </p>
      <p className='pb-2 text-sm font-medium leading-[14px] text-[#707281]'>
        {bookingStore.startDate && bookingStore.endDate ? `${format(parseISO(bookingStore.startDate), "MMM dd, yyyy")} - ${format(parseISO(bookingStore.endDate), "MMM dd, yyyy")}` : t("calender.footer.text")}
        {/* Add your travel dates for exact pricing */}
      </p>
    </div>;
};
const DateInputmodalFooter = ({
  handleDateReset = () => {}
}: {
  handleDateReset: () => void;
}) => {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const isPaymentPage = pathname?.startsWith("/payment");
  return <>
      <Divider data-sentry-element="Divider" data-sentry-source-file="DateSelector.tsx" />
      <div className={`flex w-full items-center gap-4 px-6 py-4 shadow-[0_-4px_7px_0_rgba(55,56,77,0.08)] ${isPaymentPage ? "justify-center" : "justify-between"}`}>
        {/* <div className='-mb-2 w-full'>
          <PriceAndRating
            property={property}
            isSticky={true}
            shouldShowNighlyPriceOnly={true}
            displayAllInPricing={displayAllInPricing}
          />
         </div> */}
        {!isPaymentPage && <button type='button' onClick={handleDateReset} className='btn btn-secondary w-1/2 min-w-[unset] rounded-lg border-[#BCC1D6] text-base font-semibold leading-7 text-[#53535D] msm:min-w-[140px]'>
            Clear dates
          </button>}
        <button type='button' className='btn btn-primary w-1/2 min-w-[unset]  rounded-lg px-10 py-3 text-base font-bold leading-7 hover:bg-[#015764] msm:min-w-[140px]' onClick={() => {
        closeDetailPageCalendar();
        dispatch(markBookingStore({
          isCalendarOpen: false
        }));
      }}>
          Save
        </button>
      </div>
    </>;
};
const DateSelectorForBookingFlow = ({
  property,
  usedFromContactHost,
  isDisplayClearDates = false,
  displayAllInPricing = false
}: DateSelectorProps) => {
  const bookingStore = useAppSelector(selectBookingStore);
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isSmallerThan992] = useMediaQuery("(max-width: 992px)");
  const isPaymentPage = pathname?.startsWith("/payment");
  const isMultiUnit = searchParams?.get("fromMultiUnit") || "";
  const [focusedInput, setFocusedInput] = useState<FocusedInput>(null);
  const [bookingStartDate, setBookingStartDate] = useState<string | null>(bookingStore.startDate);
  const [bookingEndDate, setBookingEndDate] = useState<string | null>(bookingStore.endDate);
  const dataTokenValue = searchParams?.get("dataToken");
  const isDirect = searchParams?.get("isdirect");
  const whimReferral = searchParams?.get("whimreferral");
  const isGoogle = searchParams?.get("isgoogle");
  useEffect(() => {
    if (!bookingStartDate && bookingStore.startDate && !bookingEndDate && bookingStore.endDate) {
      setBookingStartDate(bookingStore.startDate);
      setBookingEndDate(bookingStore.endDate);
    }
  }, [bookingStore.startDate, bookingStore.endDate, bookingStartDate, bookingEndDate]);

  // DONE
  const onFocusChange = useCallback((focusInput: FocusedInput) => {
    // when calendar hook changes its focus fo start date or end date, we need to update our local state
    setFocusedInput(focusInput);
  }, []);
  const user = useAppSelector(selectUser);
  const isAdmin = user?.isAdmin;

  // DONE
  const onDatesChange = useCallback((newDates: OnDatesChangeProps) => {
    if (!newDates.focusedInput && newDates.startDate === newDates.endDate) {
      return;
    }
    const startDate = newDates.startDate ? format(newDates.startDate, "yyyy-MM-dd") : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedStart = newDates.startDate ? new Date(newDates.startDate) : null;
    if (selectedStart) {
      selectedStart.setHours(0, 0, 0, 0);
    }
    const isSameDayStart = selectedStart?.getTime() === today.getTime();
    let endDate = newDates.endDate ? format(newDates.endDate, "yyyy-MM-dd") : null;
    if (newDates.focusedInput === END_DATE) {
      //means first date is selected now, so clear the any end date which is previously selected
      endDate = null;
    }
    setBookingStartDate(startDate);
    setBookingEndDate(endDate);
    setFocusedInput(newDates.focusedInput);
    if (startDate && endDate && newDates.focusedInput !== END_DATE &&
    // means both dates are selected
    bookingStore.adultNumber + bookingStore.childNumber <= (property.max_no_guests || 1)) {
      const dayCount = differenceInDays(parseISO(endDate), parseISO(startDate));
      dispatch(markBookingStore({
        startDate,
        endDate,
        // remove promo code on date change
        discount: 0,
        promocode: null
      }));
      closeDetailPageCalendar();
      dispatch(markBookingStore({
        dayCount,
        isCalendarOpen: false
      }));

      // remove savedPrice value, searchPrice, source and quoteId from url
      const updatedURL = removeQueryParameters(window.location.href, ["savedPrice", "searchPrice", "source", "quoteId"]);
      const startDateUrl = updateQueryStringParameter(updatedURL, isPaymentPage ? "startDate" : "check_in", startDate);
      const endDateURL = updateQueryStringParameter(startDateUrl, isPaymentPage ? "endDate" : "check_out", endDate);

      // Update endDateURL with dayCount
      const updatedEndDateURL = updateQueryStringParameter(endDateURL, "dayCount", differenceInDays(new Date(endDate), new Date(startDate)).toString());
      let finalURL = isPaymentPage ? updatedEndDateURL : endDateURL;
      const existingAllowSameDay = searchParams?.get("allowSameDay");
      if (isSameDayStart) {
        finalURL = updateQueryStringParameter(finalURL, "allowSameDay", "true");
      } else if (existingAllowSameDay === "true" && !isAdmin) {
        // preserve existing allowSameDay
        finalURL = updateQueryStringParameter(finalURL, "allowSameDay", "true");
      } else {
        finalURL = removeQueryParameters(finalURL, ["allowSameDay"]);
      }
      window.history.pushState({
        path: finalURL
      }, "", finalURL);
      void (async () => {
        const mixpanelV1Client = (await import("services/mixpanel")).mixpanelV1Client;
        mixpanelV1Client.CALENDER({
          startDate,
          endDate
        });
      })();
    }
  }, [bookingStore.adultNumber, bookingStore.childNumber, dispatch, isPaymentPage, property.max_no_guests]);

  // DONE
  useEffect(() => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const timestemp2: string = params.get("timestamp") || "";
    const dateData: string = localStorage.getItem("backClick") || "";
    if (dateData) {
      const {
        endDate,
        startDate,
        timestamps,
        adultsCount,
        childrenCount,
        petNumber
      } = (JSON.parse(dateData) as {
        adultsCount: string;
        childrenCount: string;
        petNumber: string;
        endDate: string;
        startDate: string;
        timestamps: string;
      });
      const startDateUrl = updateQueryStringParameter(window.location.href, "check_in", startDate);
      const endDateURL = updateQueryStringParameter(startDateUrl, "check_out", endDate);
      if (timestamps === timestemp2 && timestamps && timestemp2) {
        window.history.pushState({
          path: endDateURL
        }, "", endDateURL);
        dispatch(markBookingStore({
          startDate,
          endDate,
          dayCount: differenceInDays(new Date(endDate), new Date(startDate)),
          adultNumber: +adultsCount || 1,
          childNumber: +childrenCount || 0,
          petNumber: +petNumber || 0
        }));
      }
    }
  }, [dispatch]);

  // DONE
  const onReset = useCallback(() => {
    // reset precision pricing store data
    dispatch(clearPrecisionPricingData());
    dispatch(markBookingStore({
      startDate: null,
      endDate: null,
      dayCount: 0,
      prequote: null,
      hasPrequote: false,
      buttonText: "Check Availability",
      pet: false,
      adultNumber: 1,
      childNumber: 0,
      petNumber: 0,
      isDateAvailable: false
    }));
    setFocusedInput(START_DATE);
    const googleParams = isGoogle === "true" ? "?isgoogle=true" : "";
    const referralDirect = isDirect === "true" ? `${googleParams ? "&" : "?"}isdirect=true` : "";
    const whimreferral = whimReferral ? `${googleParams || referralDirect ? "&" : "?"}whimreferral=${whimReferral || ""}` : "";
    const dataToken = dataTokenValue === "E76xOs7B2s0" ? `${googleParams || referralDirect || whimreferral ? "&" : "?"}dataToken=E76xOs7B2s0` : "";
    const fromMultiUnit = searchParams?.get("fromMultiUnit") ? `${googleParams || referralDirect || whimreferral ? "&" : "?"}fromMultiUnit=${searchParams?.get("fromMultiUnit")}` : "";
    const allowedSameDay = searchParams?.get("allowSameDay") ? `${googleParams || referralDirect || whimreferral || dataToken ? "&" : "?"}allowSameDay=${searchParams?.get("allowSameDay")}` : "";
    const url = clearQueryStringParameter(window.location.href);
    const newURL = url + googleParams + referralDirect + whimreferral + dataToken + fromMultiUnit + allowedSameDay;
    window.history.pushState({
      path: newURL
    }, "", newURL);
  }, [dispatch, dataTokenValue, isDirect, whimReferral, isGoogle]);

  // DONE
  useEffect(() => {
    if (bookingStore.isLoading === true) {
      onFocusChange(null); // when booking store is loading means now we can start reset calendar to initial state
    }
  }, [bookingStore.isLoading, onFocusChange]);
  const {
    onResetDates
  } = useDatepicker({
    startDate: new Date(bookingStartDate || ""),
    endDate: new Date(bookingEndDate || ""),
    focusedInput,
    onDatesChange
  });

  // for below 435px screen size
  const handleDateReset = useCallback(() => {
    onResetDates();
    onReset();
  }, [onReset, onResetDates]);
  const {
    formattedBeforeDate
  } = calculateFormattedBeforeDate(bookingStore?.startDate, property?.cancellation_policy?.policy?.[0]?.validTo || 0);
  const handleCloseCalendar = useCallback(() => {
    dispatch(markBookingStore({
      isCalendarOpen: false
    }));
    if (!bookingStore.startDate || !bookingStore.endDate) {
      handleDateReset();
    }
    setFocusedInput(START_DATE);
  }, [bookingStore.endDate, bookingStore.startDate, dispatch, handleDateReset]);
  const handleOpenCalendar = useCallback(() => {
    onFocusChange(START_DATE);
    dispatch(markBookingStore({
      isCalendarOpen: true
    }));
    void dispatch(registerClickDateChangeEventMixPanel({
      property_id: property?.property_id,
      city: property?.city,
      state: property?.state,
      property_rating_star_sum: property.property_rating_star_sum,
      property_rating_user_count: property.property_rating_user_count,
      isMultiUnit: isMultiUnit === "true",
      // to check user comes from multi units detail page or not
      guestCount: bookingStore?.adultNumber + bookingStore?.childNumber || 0,
      cancellationPolicy: property?.cancellation_policy?.policy?.[0]?.id === CancellationPolicyType.PARTIAL ? `Cancel on or before ${formattedBeforeDate} for a partial refund.` : `Cancel on or before ${formattedBeforeDate} for a refund.`
    }));
  }, [dispatch, onFocusChange]);
  useEffect(() => {
    if (bookingStore.isCalendarOpen && focusedInput === null) {
      onFocusChange(START_DATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingStore.isCalendarOpen]);
  const maxBookingDate = addDays(new Date(), focusedInput === "startDate" ? bookingStore.calendarRateList.length - 1 : bookingStore.calendarRateList.length);
  const allowSameDay = searchParams?.get("allowSameDay") === "true" || isAdmin;
  const getTodayMidnight = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };
  return <>
      <CalendarAndGuestContols displayAllInPricing={displayAllInPricing} isDisabled={false} startDate={bookingStartDate && toDate(parseISO(bookingStartDate)) || null} endDate={bookingEndDate && toDate(parseISO(bookingEndDate)) || null} isOpen={bookingStore.isCalendarOpen} onClose={handleCloseCalendar} onOpen={handleOpenCalendar} isDateSelected={bookingStore.isDateAvailable} data-sentry-element="CalendarAndGuestContols" data-sentry-source-file="DateSelector.tsx">
        <GuestModule property={property} displayAllInPricing={displayAllInPricing} data-sentry-element="GuestModule" data-sentry-source-file="DateSelector.tsx" />
      </CalendarAndGuestContols>

      <DateRangeInputDetail usedFromContactHost={usedFromContactHost} key='dateRangeInputDetail' isOpen={bookingStore.isCalendarOpen} onClose={handleCloseCalendar} onOpen={handleOpenCalendar} minBookingDate={allowSameDay ? getTodayMidnight() : parseISO(bookingStore.calendarRateList[0]?.valid_from)} maxBookingDate={maxBookingDate} onDatesChange={onDatesChange} startDate={bookingStartDate && toDate(parseISO(bookingStartDate)) || null} endDate={bookingEndDate && toDate(parseISO(bookingEndDate)) || null} focusedInput={focusedInput} minBookingDays={bookingStore.calendarRateList[0]?.min_nights + 1 || 2} calendarRates={bookingStore.calendarRateList} onReset={onReset}
    // isDisabled={isPaymentPage}
    checkArrivalDeparture={bookingStore.calendarData.checkArrivalDeparture} vertical={isSmallerThan992} modalHeader={<DateInputmodalHeader />} modalFooter={<DateInputmodalFooter handleDateReset={handleDateReset} />} shouldMaximizeProfitBasedOnPmsSystem={bookingStore.maximizeProfit} isDisplayClearDates={isDisplayClearDates} data-sentry-element="DateRangeInputDetail" data-sentry-source-file="DateSelector.tsx" />
    </>;
};
export default withErrorBoundary(DateSelectorForBookingFlow, {
  beforeCapture: scope => {
    scope.setTag("ws_location", "DateSelectorForBookingFlow");
  }
});